import React, { useRef, useState, useEffect } from "react";
import ModalLoading from './ModalLoading.js';
/**
 * プレイリスト風オーディオプレイヤー
 * - 各曲に独立した Play / Pause ボタンを表示
 * - 再生中の曲はハイライト表示
 * - 曲が終わると自動で次の曲へ（最後の曲の後は先頭へループ）
 * ※ Tailwind CSS を想定していますが、クラスは自由に変更してください
 */
const AudioPlaylist = ({ fileList }) => {
    const audioRefs = useRef([]);              // 各 <audio> の参照を保持
    const [currentIndex, setCurrentIndex] = useState(null); // 再生中インデックス（null = まだ再生していない）
    const [progress, setProgresses] = useState({}); // 曲ごとの進行状況
    const [modalloading, setmodalLoading] = useState(false);
    const [progressPercentage, setProgressPercentage] = useState(0);
    // 対象のファイルだけ抽出
    const audioFiles = (fileList || []).filter((file) => {
        const name = file?.filename || file?.file;
        if (typeof name !== "string") return false;

        const ext = name.split(".").pop().toLowerCase();
        return ["mp3", "wav", "ogg", "aac", "flac", "m4a"].includes(ext);
    });

    /**
     * 指定インデックスの曲を再生。
     * すでに別の曲が再生中であれば一旦停止してから切り替える。
     */
    const playTrack = async (index) => {
        // モーダル表示をONに
        setmodalLoading(true);
        // 現在再生中の曲を停止
        if (currentIndex !== null && audioRefs.current[currentIndex]) {
            const now = audioRefs.current[currentIndex];
            now.pause();
            now.currentTime = 0;
        }

        // 新しい曲を再生
        const target = audioRefs.current[index];
        console.log("再生対象audioタグのsrc:", target?.src);
        if (target) {
            try {
                await target.play(); // 非同期処理
                setCurrentIndex(index);
            } catch (err) {
                console.warn("再生エラー", err);
            } finally {
                // 再生が始まった or エラーが出たらローディングを消す
                setmodalLoading(false);
            }
        } else {
            setmodalLoading(false);
        }
    };

    /**
     * 再生終了時に次の曲へ進める（最後の曲の後は 0 にループ）。
     * useEffect で currentIndex が変わったタイミングで該当 audio に listener を付与。
     */
    useEffect(() => {
        if (currentIndex === null) return; // まだ何も再生していない

        const currentAudio = audioRefs.current[currentIndex];
        if (!currentAudio) return;

        const handleEnded = () => {
            const next = (currentIndex + 1) % audioFiles.length; // ループ
            playTrack(next);
        };
        // const handleTimeUpdate = () => {
        //     const { currentTime, duration } = currentAudio;
        //     setProgresses((prev) => ({
        //         ...prev,
        //         [currentIndex]: duration ? currentTime / duration : 0,
        //     }));
        // };
        const handleTimeUpdate = (index) => {
            const audio = audioRefs.current[index];
            if (audio && audio.duration) {
                const newProgress = audio.currentTime / audio.duration;
                
                setProgresses((prev) => ({
                    ...prev,
                    [index]: newProgress,
                }));
            }
        };
        const handleTimeUpdateWrapper = () => handleTimeUpdate(currentIndex);
        currentAudio.addEventListener("ended", handleEnded);
        currentAudio.addEventListener("timeupdate", handleTimeUpdateWrapper);

        return () => {
            currentAudio.removeEventListener("ended", handleEnded);
            currentAudio.removeEventListener("timeupdate", handleTimeUpdateWrapper);
        };
    }, [currentIndex, audioFiles.length]);
    
    const handleSeek = (index, value) => {
        const audio = audioRefs.current[index];
        if (audio && audio.duration) {
            audio.currentTime = (value / 100) * audio.duration;
        }
    };
    
    // トラック番号順でソート
    const sortedAudioFiles = [...audioFiles].sort((a, b) => {
        const getTrackNumber = (file) => {
            const name = file?.filename || file?.file || "";
            
            const match = name.match(/^(\d+)/);  // 先頭の連番を取得
            return match ? parseInt(match[1], 10) : 9999;  // 見つからない場合は末尾へ
        };
        return getTrackNumber(a.filename) - getTrackNumber(b.filename);
    });
    
    
    
    return (
        <div className="flex flex-col gap-3 w-full max-w-md mx-auto">
            {sortedAudioFiles.map((file, index) => {
                const name = file?.filename || file?.file || "";
                const isPlaying = index === currentIndex && !audioRefs.current[index]?.paused;
                return (
                    <div
                        key={index}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: 12,
                            border: "1px solid #ddd",
                            borderRadius: 12,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            backgroundColor: isPlaying ? "#e0f0ff" : "#fff",
                            transition: "background-color 0.3s ease",
                        }}
                    >
                        {/* ファイル名 */}
                        <span style={{ flex: 1, marginRight: 8, fontSize: 14, fontWeight: "500", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {name}
                        </span>

                        {/* 再生 / 一時停止ボタン */}
                        <button
                            onClick={() => {
                                if (isPlaying) {
                                    // すでに再生中 → 一時停止
                                    audioRefs.current[index].pause();
                                    setCurrentIndex(null);  
                                } else {
                                    playTrack(index);
                                }
                            }}
                            style={{
                                fontSize: 18,
                                padding: "4px 12px",
                                borderRadius: 20,
                                border: "1px solid #ccc",
                                cursor: "pointer",
                                userSelect: "none",
                                backgroundColor: "#fff",
                            }}
                            title={isPlaying ? "Pause" : "Play"}
                        >
                            {isPlaying ? "⏸" : "▶️"}
                        </button>
                        {/* シークバー */}
                        <input
                            type="range"
                            min="0"
                            max="100"
                            step="0.1"
                            value={(progress [index] || 0)* 100}
                            onChange={(e) => handleSeek(index, parseFloat(e.target.value))}
                            className="w-full"
                            style={{
                                accentColor: "#3b82f6", // Tailwind blue-500
                                cursor: "pointer",
                            }}
                        />
                        {/* audio 要素（コントロールは隠す） */}
                        <div style={{ opacity:0.5 }}>
                            {modalloading && <ModalLoading isModal={true} progressPercentage={progressPercentage} />}
                            <audio
                                ref={(el) => (audioRefs.current[index] = el)}
                                src={file.token}
                                playsInline
                                preload="metadata"
                                className="hidden"
                                type={
                                    name.endsWith(".m4a") ? "audio/mp4" :
                                        name.endsWith(".mp3") ? "audio/mpeg" :
                                            name.endsWith(".wav") ? "audio/wav" :
                                                name.endsWith(".ogg") ? "audio/ogg" :
                                                    undefined
                                }
                            />
                        </div> 
                    </div>
                );
            })}
        </div>
    );
};

export default AudioPlaylist;
